import React from 'react';
import classnames from 'classnames';
import PageSectionFeaturedCollections from '../components/Page/SectionFeaturedCollections';
import PageSectionCollectionsGrid from '../components/Page/SectionCollectionsGrid';
import PageSectionCollectionSlider from '../components/Page/SectionCollectionSlider';
import PageSectionCollectionFullWidth from '../components/Page/SectionCollectionFullWidth';
import PageSectionHTML from '../components/Page/SectionHTML';
import PageSectionDivider from '../components/Page/SectionDivider';
import { getIntl } from './IntlInstance';

import { getSetting } from './Settings';
import { isValidUrl } from './Validators';

export const canDownloadPageItem = item => !!item?.allow_download;

export const sectionComponents = {
  PageSectionFeaturedCollections,
  PageSectionCollectionsGrid,
  PageSectionCollectionSlider,
  PageSectionCollectionFullWidth,
  PageSectionHTML,
  PageSectionDivider,
};

export const sections = (intlInstance) => {
  let intl;
  if (!intlInstance) {
    intl = getIntl();
  } else {
    intl = intlInstance;
  }

  return [
    {
      section: 'collection-full-width',
      name: intl.formatMessage({ defaultMessage: 'Featured' }),
      description: intl.formatMessage({ defaultMessage: 'One full-width Collection' }),
      icon: 'fa-rectangle',
      backendComponent: 'AdminPageSectionsCollectionFullWidth',
      frontendComponent: 'PageSectionCollectionFullWidth',
      collection: null,
    },
    {
      section: 'featured-collections',
      name: intl.formatMessage({ defaultMessage: 'Grid' }),
      description: intl.formatMessage({ defaultMessage: 'Collections in a Grid' }),
      icon: 'fa-grid',
      backendComponent: 'AdminPageSectionsCollectionsGrid',
      frontendComponent: 'PageSectionFeaturedCollections',
      collections: [],
    },
    {
      section: 'collections-grid',
      name: intl.formatMessage({ defaultMessage: 'Masonry' }),
      description: intl.formatMessage({ defaultMessage: 'Collections in masonry view' }),
      icon: 'fa-objects-column',
      backendComponent: 'AdminPageSectionsCollectionsGrid',
      frontendComponent: 'PageSectionCollectionsGrid',
      collections: [],
    },
    {
      section: 'collection-slider',
      name: intl.formatMessage({ defaultMessage: 'Slider' }),
      description: intl.formatMessage({ defaultMessage: 'Collection in a slider' }),
      icon: 'fa-gallery-thumbnails',
      backendComponent: 'AdminPageSectionsCollectionSlider',
      frontendComponent: 'PageSectionCollectionSlider',
      collections: [],
    },
    {
      section: 'html',
      name: intl.formatMessage({ defaultMessage: 'HTML' }),
      description: intl.formatMessage({ defaultMessage: 'Text section (HTML)' }),
      icon: 'fa-code',
      backendComponent: 'AdminPageSectionsHTML',
      frontendComponent: 'PageSectionHTML',
      content: '',
    },
    {
      section: 'divider',
      name: intl.formatMessage({ defaultMessage: 'Divider' }),
      description: intl.formatMessage({ defaultMessage: 'Page divider' }),
      icon: 'fa-horizontal-rule',
      backendComponent: 'AdminPageSectionsDivider',
      frontendComponent: 'PageSectionDivider',
      style: 'solid',
      size: 1,
      color: '#999999',
    },
  ];
};

export const getSection = sectionId => (
  sections().find(s => (s.section === sectionId))
);

export const getSectionField = (sectionId, field) => {
  const section = getSection(sectionId);
  return (section && section[field]) ? section[field] : null;
};

export const getSectionName = sectionId => (
  getSectionField(sectionId, 'name')
);

export const getSectionIcon = sectionId => (
  getSectionField(sectionId, 'icon')
);

export const getSectionBackendComponent = sectionId => (
  getSectionField(sectionId, 'backendComponent')
);

export const getSectionFrontendComponent = sectionId => (
  getSectionField(sectionId, 'frontendComponent')
);

export const getPageUrl = (page, withHost = false) => {
  let urlPrefix = 'content';
  if (!page.options.page_type || page.options.page_type === 'fullscreen') {
    urlPrefix = 'pages';
  }
  if (withHost) {
    return `${window.location.origin}/${urlPrefix}/${page.url}`;
  }
  return `/${urlPrefix}/${page.url}`;
};

export const getClosedSections = () => {
  const settings = getSetting('page_editor_settings');
  if (settings && settings.closed_sections) {
    return settings.closed_sections;
  }
  return [];
};

export const isSectionClosed = sectionId => (
  getClosedSections().includes(sectionId)
);

const isFeatureCollectionsEnabled = (value) => (
  value === true
  || value === 'true'
  || value === 1
  || value === '1'
  || value === 'yes'
);

export const shouldDisableTransparentHeaderPadding = (pageSections = []) => {
  const firstPuckSection = pageSections[0] || null;

  if (!firstPuckSection || firstPuckSection.type !== 'Collection') {
    return false;
  }

  return isFeatureCollectionsEnabled(firstPuckSection.featureCollections);
};

export const getTransparentHeaderPaddingClass = (header, disablePadding, showSearchBar = false) => {
  const hasOverlayHeader = header === 'transparent' || header === 'solid';
  const className = classnames({
    'page-header-transparent-no-padding': hasOverlayHeader && disablePadding,
    'page-header-with-search': hasOverlayHeader && showSearchBar,
  });

  return className ? ` ${className}` : '';
};

const renderPageSectionPlaceholderElement = ({
  icon,
  className = '',
}) => {
  const placeholderClassName = classnames('puck-section-placeholder', className);

  return (
    <div className={placeholderClassName} aria-hidden="true">
      <div className="puck-section-placeholder-icon-wrap">
        <i className={`far ${icon} puck-section-placeholder-icon`} />
      </div>
    </div>
  );
};

export const getCollectionPlaceholderElement = ({ className = '' } = {}) => (
  renderPageSectionPlaceholderElement({
    icon: 'fa-layer-group',
    className,
  })
);

export const getFilePlaceholderElement = ({ className = '' } = {}) => (
  renderPageSectionPlaceholderElement({
    icon: 'fa-file-image',
    className,
  })
);

export const getVideoPlaceholderElement = ({ className = '' } = {}) => (
  renderPageSectionPlaceholderElement({
    icon: 'fa-file-video',
    className,
  })
);

export const getPageFileRoute = ({
  pageBasePath = 'pages',
  pageRouteId,
  pageId,
  file,
}) => {
  if (!pageRouteId || !file?.uuid) {
    return null;
  }

  return {
    pathname: `/${pageBasePath}/${pageRouteId}/file/${file.uuid}`,
    state: {
      modal: true,
      pageId,
      pageRouteId,
      pageFile: file,
    },
  };
};

export const normalizePageVisibilityMode = (value) => {
  if (value === false || value === 'false' || value === 'public') {
    return 'public';
  }

  return value;
};

export const getCollectionDestination = ({
  collection,
  pageVisibilityMode,
  pageBasePath = 'pages',
  pageRouteId,
  isLoggedIn = true,
  view = null,
}) => {
  const normalizedVisibilityMode = normalizePageVisibilityMode(pageVisibilityMode);
  const collectionUuid = collection?.uuid;

  if (!collectionUuid) {
    return '';
  }

  const customUrl = collection?.custom_url;

  if (isLoggedIn === false && isValidUrl(customUrl)) {
    return customUrl;
  }

  const destination =
    normalizedVisibilityMode === 'public' && pageRouteId
      ? `/${pageBasePath}/${pageRouteId}/collection/${collectionUuid}`
      : `/collections/${collectionUuid}`;

  if (view) {
    const params = new URLSearchParams({ view });
    return `${destination}?${params.toString()}`;
  }

  return destination;
};

export const getFileDestination = ({
  file,
  pageBasePath = 'pages',
  pageRouteId,
  pageId,
}) => {
  const fileUuid = file?.uuid;

  if (!fileUuid) {
    return null;
  }

  return getPageFileRoute({
    pageBasePath,
    pageRouteId,
    pageId,
    file,
  }) || `/files/${fileUuid}`;
};

let pageFileModalLockCount = 0;

const PAGE_FILE_MODAL_CLASS = 'page-file-modal-open';

const togglePageFileModalClass = (shouldLock) => {
  if (typeof document === 'undefined') {
    return;
  }

  document.body.classList.toggle(PAGE_FILE_MODAL_CLASS, shouldLock);
  document.documentElement.classList.toggle(PAGE_FILE_MODAL_CLASS, shouldLock);
};

export const setPageFileModalScrollLock = (shouldLock) => {
  if (shouldLock) {
    pageFileModalLockCount += 1;
    if (pageFileModalLockCount === 1) {
      togglePageFileModalClass(true);
    }
    return;
  }

  pageFileModalLockCount = Math.max(0, pageFileModalLockCount - 1);
  if (pageFileModalLockCount === 0) {
    togglePageFileModalClass(false);
  }
};

export const isPageRoute = () => {
  const { location } = window;
  return /^\/(?:pages|content)(?:\/|$)/.test(location.pathname);
};

export const isSearchPageRoute = () => {
  const { location } = window;
  return /^\/pages\/[^/]+\/search\/?$/.test(location.pathname);
};
